import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';

const varianceColor = (v) => {
  if (v === undefined || v === null || v === 0) return 'text.primary';
  return v > 0 ? 'success.main' : 'error.main';
};

/**
 * One section (Assets / Liabilities / Equity) of the balance sheet, as
 * returned by BalanceSheetService: { groups: [{key,label,lines,total,
 * compare_total}], total }. Each line already carries its own `indent`/
 * `is_total` from the API - there is one collapse level here (per group),
 * matching what the API actually groups by (balance sheet section), not an
 * arbitrary deeper account tree the API doesn't return.
 */
const Section = ({ title, section, hasCompare, expanded, onToggle }) => {
  const groups = section?.groups ?? [];
  return (
    <>
      <TableRow>
        <TableCell colSpan={hasCompare ? 4 : 2} sx={{ fontWeight: 700, fontSize: '1.05rem', pt: 3, border: 0 }}>
          {title}
        </TableCell>
      </TableRow>
      {groups.map((group) => {
        const isOpen = expanded.has(group.key);
        return (
          <React.Fragment key={group.key}>
            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onToggle(group.key)}>
              <TableCell sx={{ fontWeight: 600 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                  {group.label}
                </Stack>
              </TableCell>
              {hasCompare && <TableCell />}
              {hasCompare && <TableCell />}
              <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatAccountingAmount(group.total)}
              </TableCell>
            </TableRow>
            {isOpen && group.lines.map((line, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ pl: 2 + (line.indent || 0) * 2, color: 'text.secondary' }}>
                  {line.label}
                </TableCell>
                {hasCompare && (
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {line.compare_amount !== undefined ? formatAccountingAmount(line.compare_amount) : ''}
                  </TableCell>
                )}
                {hasCompare && (
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: varianceColor(line.variance) }}>
                    {line.variance !== undefined ? formatAccountingAmount(line.variance) : ''}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatAccountingAmount(line.amount, { blankZero: true })}
                </TableCell>
              </TableRow>
            ))}
          </React.Fragment>
        );
      })}
      <TableRow sx={{ bgcolor: 'action.selected' }}>
        <TableCell sx={{ fontWeight: 700 }}>Total {title}</TableCell>
        {hasCompare && <TableCell />}
        {hasCompare && <TableCell />}
        <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {formatAccountingAmount(section?.total)}
        </TableCell>
      </TableRow>
    </>
  );
};

const BalanceSheetReport = ({ data, loading }) => {
  const allKeys = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.assets?.groups ?? []),
      ...(data.liabilities?.groups ?? []),
      ...(data.equity?.groups ?? []),
    ].map((g) => g.key);
  }, [data]);

  const [expanded, setExpanded] = useState(new Set());
  // Default to fully expanded whenever a new report is generated.
  useEffect(() => {
    if (data) setExpanded(new Set(allKeys));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.generated_at]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(allKeys));
  const collapseAll = () => setExpanded(new Set());

  if (loading) {
    return <Typography color="text.secondary">Generating balance sheet...</Typography>;
  }
  if (!data) {
    return <EmptyRecordsState message="Choose a period and generate the report." />;
  }

  const hasCompare = Boolean(data.compare_name);

  const handleExport = () => {
    const headers = ['Section', 'Account', 'Amount'];
    const rows = [];
    const pushSection = (title, section) => {
      (section?.groups ?? []).forEach((g) => {
        g.lines.forEach((l) => rows.push([title, l.label, l.amount]));
        rows.push([title, `Total ${g.label}`, g.total]);
      });
      rows.push([title, `Total ${title}`, section?.total ?? 0]);
    };
    pushSection('Assets', data.assets);
    pushSection('Liabilities', data.liabilities);
    pushSection('Equity', data.equity);
    rows.push(['', 'TOTAL ASSETS', data.total_assets]);
    rows.push(['', 'TOTAL LIABILITIES AND EQUITY', data.total_liab_equity]);
    downloadCsv(`balance-sheet-${data.period_name || 'report'}.csv`, headers, rows);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Balance Sheet</Typography>
          <Typography variant="body2" color="text.secondary">
            {data.period_name}{data.fiscal_year_name ? ` · ${data.fiscal_year_name}` : ''}
            {hasCompare ? ` (vs ${data.compare_name})` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={data.is_balanced ? <CheckCircleIcon /> : <ErrorIcon />}
            label={data.is_balanced ? 'Balanced' : 'Unbalanced'}
            color={data.is_balanced ? 'success' : 'error'}
            size="small"
          />
          <IconButton size="small" onClick={expandAll} title="Expand all"><UnfoldMoreIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={collapseAll} title="Collapse all"><UnfoldLessIcon fontSize="small" /></IconButton>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>
        </Stack>
      </Stack>

      {!data.is_balanced && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Assets and Liabilities + Equity differ by {formatAccountingAmount(Math.abs(data.variance || 0))}.
        </Alert>
      )}

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
              {hasCompare && <TableCell sx={{ fontWeight: 700 }} align="right">{data.compare_name}</TableCell>}
              {hasCompare && <TableCell sx={{ fontWeight: 700 }} align="right">Variance</TableCell>}
              <TableCell sx={{ fontWeight: 700 }} align="right">{data.period_name}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <Section title="Assets" section={data.assets} hasCompare={hasCompare} expanded={expanded} onToggle={toggle} />
            <Section title="Liabilities" section={data.liabilities} hasCompare={hasCompare} expanded={expanded} onToggle={toggle} />
            <Section title="Equity" section={data.equity} hasCompare={hasCompare} expanded={expanded} onToggle={toggle} />
            {Boolean(data.equity?.current_year_earnings) && (
              <TableRow>
                <TableCell sx={{ pl: 4, color: 'text.secondary', fontStyle: 'italic' }}>Current Year Earnings (included above)</TableCell>
                {hasCompare && <TableCell />}
                {hasCompare && <TableCell />}
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', fontStyle: 'italic' }}>
                  {formatAccountingAmount(data.equity.current_year_earnings)}
                </TableCell>
              </TableRow>
            )}
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>TOTAL ASSETS</TableCell>
              {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
              {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
              <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatAccountingAmount(data.total_assets)}
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>TOTAL LIABILITIES AND EQUITY</TableCell>
              {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
              {hasCompare && <TableCell sx={{ color: 'primary.contrastText' }} />}
              <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatAccountingAmount(data.total_liab_equity)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BalanceSheetReport;
